import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Check } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { PageHeader, Spinner } from "@/components/common/Common";
import { PersonPicker } from "@/components/common/PersonPicker";
import { toOpts, useLookups } from "@/hooks/useLookups";
import { api, fmtDate, formatError } from "@/lib/api";
import { AU_STATES, ENTITIES, FEE_PAID_BY, PAYMENT_TERMS, PAY_SCHEDULES, RATE_TYPES, opts } from "@/lib/constants";
import { Field } from "@/pages/CompanyFormDialog";
import { RateBreakdown } from "@/components/common/RateBreakdown";

const STEPS = ["Contractor", "Placement", "First rate", "Review"];

function Sel({ value, onChange, options, testId, placeholder = "Choose" }) {
  return (
    <Select value={value || ""} onValueChange={onChange}>
      <SelectTrigger className="bg-white" data-testid={testId}><SelectValue placeholder={placeholder} /></SelectTrigger>
      <SelectContent>{options.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
    </Select>
  );
}

export default function NewContractWizard() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const lk = useLookups();
  const [step, setStep] = useState(0);
  const [mode, setMode] = useState("existing");
  const [contractorId, setContractorId] = useState(params.get("contractor_id"));
  const [person, setPerson] = useState({ first_name: "", last_name: "", email: "", mobile: "", date_of_birth: "", address: { state: "" } });
  const [k, setK] = useState({ employing_entity: "payme_australia", pay_schedule: "weekly", payment_terms: "7_days", payme_fee_paid_by: "contractor", workers_comp: true });
  const [rate, setRate] = useState({ rate_type: "hourly", gross_rate: "", payme_fee_percent: "", effective_from: "" });
  const [busy, setBusy] = useState(false);
  const sk = (key) => (v) => setK((s) => ({ ...s, [key]: v?.target ? v.target.value : v }));
  const sp = (key) => (e) => setPerson((s) => ({ ...s, [key]: e.target.value }));

  const valid = [
    mode === "existing" ? !!contractorId : person.first_name && person.last_name,
    k.recruitment_company_id && k.start_date && (!k.end_date || k.end_date >= k.start_date) && (k.employing_entity !== "pte_company" || k.pte_company_id),
    true, true,
  ];
  const name = (list, id) => list?.find((x) => x.id === id)?.name || "—";

  const create = async () => {
    setBusy(true);
    try {
      const body = { contract: k, first_rate: rate.gross_rate ? rate : null };
      if (mode === "existing") body.contractor_id = contractorId;
      else body.new_contractor = { ...person, address: { state: person.address.state || null }, date_of_birth: person.date_of_birth || null };
      const { data } = await api.post("/contracts", body);
      toast.success(`Contract ${data.employment_id} created`);
      navigate(`/contracts/${data.id}`);
    } catch (e) { toast.error(formatError(e)); } finally { setBusy(false); }
  };

  return (
    <div className="max-w-4xl">
      <PageHeader title="New contract" subtitle="Creates the contract with status New join." testId="wizard-header" />
      <ol className="flex gap-2 mb-6" data-testid="wizard-steps">
        {STEPS.map((s, i) => (
          <li key={s} className={`flex-1 flex items-center gap-2 rounded-md border px-3 py-2 text-xs sm:text-sm ${i === step ? "bg-blue-50 border-blue-200 text-blue-700 font-medium" : i < step ? "bg-white border-slate-200 text-slate-700" : "bg-white border-slate-200 text-slate-400"}`}>
            <span className={`h-5 w-5 rounded-full flex items-center justify-center text-[11px] ${i < step ? "bg-emerald-500 text-white" : i === step ? "bg-blue-600 text-white" : "bg-slate-200"}`}>{i < step ? <Check className="h-3 w-3" /> : i + 1}</span>
            <span className="hidden sm:inline">{s}</span>
          </li>
        ))}
      </ol>
      <div className="bg-white border border-slate-200 rounded-lg shadow-sm p-6 animate-fade-up" key={step}>
        {step === 0 && (
          <div className="space-y-4">
            <div className="flex gap-2">
              {["existing", "new"].map((m) => (
                <button key={m} type="button" onClick={() => setMode(m)} data-testid={`wizard-mode-${m}`}
                  className={`rounded-full border px-4 py-1.5 text-sm ${mode === m ? "bg-blue-600 border-blue-600 text-white" : "bg-white border-slate-200 text-slate-600"}`}>
                  {m === "existing" ? "Existing contractor" : "New contractor"}
                </button>
              ))}
            </div>
            {mode === "existing" ? (
              <Field label="Contractor *"><PersonPicker value={contractorId} onChange={setContractorId} personType="contractor" testId="wizard-contractor-picker" placeholder="Search contractors" /></Field>
            ) : (
              <div className="grid sm:grid-cols-2 gap-4">
                <Field label="First name *"><Input value={person.first_name} onChange={sp("first_name")} data-testid="wizard-first-name" /></Field>
                <Field label="Last name *"><Input value={person.last_name} onChange={sp("last_name")} data-testid="wizard-last-name" /></Field>
                <Field label="Email"><Input type="email" value={person.email} onChange={sp("email")} data-testid="wizard-email" /></Field>
                <Field label="Mobile"><Input value={person.mobile} onChange={sp("mobile")} data-testid="wizard-mobile" /></Field>
                <Field label="Date of birth"><Input type="date" value={person.date_of_birth} onChange={sp("date_of_birth")} data-testid="wizard-dob" /></Field>
                <Field label="State"><Sel value={person.address.state} onChange={(v) => setPerson((s) => ({ ...s, address: { state: v } }))} options={AU_STATES.map((s) => ({ value: s, label: s }))} testId="wizard-person-state" /></Field>
              </div>
            )}
          </div>
        )}
        {step === 1 && (
          <div className="grid sm:grid-cols-2 gap-4">
            <Field label="Recruitment company *"><Sel value={k.recruitment_company_id} onChange={sk("recruitment_company_id")} options={toOpts(lk?.recruitment_companies)} testId="wizard-company" /></Field>
            <Field label="Recruiter"><PersonPicker value={k.recruiter_id} onChange={sk("recruiter_id")} personType="recruiter_contact" testId="wizard-recruiter" /></Field>
            <Field label="Employing entity"><Sel value={k.employing_entity} onChange={sk("employing_entity")} options={opts(ENTITIES)} testId="wizard-entity" /></Field>
            {k.employing_entity === "pte_company" && <Field label="PTE company *"><Sel value={k.pte_company_id} onChange={sk("pte_company_id")} options={toOpts(lk?.pte_companies)} testId="wizard-pte" /></Field>}
            <Field label="Position"><Sel value={k.job_position_id} onChange={sk("job_position_id")} options={toOpts(lk?.job_positions)} testId="wizard-position" /></Field>
            <Field label="Location"><Sel value={k.job_location_id} onChange={sk("job_location_id")} options={toOpts(lk?.job_locations)} testId="wizard-location" /></Field>
            <Field label="Work state"><Sel value={k.work_state} onChange={sk("work_state")} options={AU_STATES.map((s) => ({ value: s, label: s }))} testId="wizard-state" /></Field>
            <Field label="Start date *"><Input type="date" value={k.start_date || ""} onChange={sk("start_date")} data-testid="wizard-start" /></Field>
            <Field label="End date"><Input type="date" value={k.end_date || ""} onChange={sk("end_date")} data-testid="wizard-end" /></Field>
            <Field label="Pay schedule"><Sel value={k.pay_schedule} onChange={sk("pay_schedule")} options={opts(PAY_SCHEDULES)} testId="wizard-pay-schedule" /></Field>
            <Field label="Payment terms"><Sel value={k.payment_terms} onChange={sk("payment_terms")} options={opts(PAYMENT_TERMS)} testId="wizard-terms" /></Field>
            <Field label="PayMe fee paid by"><Sel value={k.payme_fee_paid_by} onChange={sk("payme_fee_paid_by")} options={opts(FEE_PAID_BY)} testId="wizard-fee-by" /></Field>
            <Field label="Assigned to"><Sel value={k.assigned_to} onChange={sk("assigned_to")} options={toOpts(lk?.staff)} testId="wizard-assigned" /></Field>
            {k.end_date && k.start_date && k.end_date < k.start_date && <p className="text-xs text-red-600 sm:col-span-2" data-testid="wizard-date-error">End date must be after the start date</p>}
          </div>
        )}
        {step === 2 && (
          <div className="grid sm:grid-cols-2 gap-4">
            <Field label="Rate type"><Sel value={rate.rate_type} onChange={(v) => setRate({ ...rate, rate_type: v })} options={opts(RATE_TYPES)} testId="wizard-rate-type" /></Field>
            <Field label="Gross rate (AUD)"><Input inputMode="decimal" className="font-mono" value={rate.gross_rate} onChange={(e) => setRate({ ...rate, gross_rate: e.target.value })} data-testid="wizard-gross-rate" /></Field>
            <Field label="PayMe fee %"><Input inputMode="decimal" className="font-mono" value={rate.payme_fee_percent} onChange={(e) => setRate({ ...rate, payme_fee_percent: e.target.value })} data-testid="wizard-fee-percent" /></Field>
            <Field label="Effective from (defaults to start date)"><Input type="date" value={rate.effective_from} onChange={(e) => setRate({ ...rate, effective_from: e.target.value })} data-testid="wizard-rate-effective" /></Field>
            <div className="sm:col-span-2"><RateBreakdown gross={rate.gross_rate} fee={rate.payme_fee_percent} effectiveFrom={rate.effective_from || k.start_date} testId="wizard-rate-breakdown" /></div>
            <p className="text-xs text-slate-400 sm:col-span-2">Optional — you can skip and add rates later. Super uses the SG % in force on the effective date.</p>
          </div>
        )}
        {step === 3 && (
          <dl className="grid sm:grid-cols-2 gap-x-8 gap-y-3 text-sm" data-testid="wizard-review">
            {[
              ["Contractor", mode === "new" ? `${person.first_name} ${person.last_name} (new)` : "Existing contractor selected"],
              ["Recruitment company", name(lk?.recruitment_companies, k.recruitment_company_id)],
              ["Employing entity", ENTITIES[k.employing_entity]],
              ["PTE company", k.employing_entity === "pte_company" ? name(lk?.pte_companies, k.pte_company_id) : "—"],
              ["Dates", `${fmtDate(k.start_date)} – ${fmtDate(k.end_date)}`],
              ["Pay schedule", PAY_SCHEDULES[k.pay_schedule]],
              ["First rate", rate.gross_rate ? `$${rate.gross_rate} ${RATE_TYPES[rate.rate_type].toLowerCase()}` : "None"],
              ["Status", "New join"],
            ].map(([a, b]) => <div key={a}><dt className="text-xs text-slate-400">{a}</dt><dd className="text-slate-800">{b}</dd></div>)}
          </dl>
        )}
      </div>
      <div className="flex justify-between mt-4">
        <Button variant="outline" onClick={() => (step ? setStep(step - 1) : navigate("/contracts"))} data-testid="wizard-back-button">{step ? "Back" : "Cancel"}</Button>
        {step < 3 ? <Button disabled={!valid[step]} onClick={() => setStep(step + 1)} data-testid="wizard-next-button">Next</Button>
          : <Button onClick={create} disabled={busy} data-testid="wizard-create-button">{busy && <Spinner className="mr-2" />}Create contract</Button>}
      </div>
    </div>
  );
}
